import React, { useEffect, useState, useCallback } from 'react';
import { Select } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActiveCustomers } from '@/redux/actions/customerActions';
import { repeatPhoneNumber } from '@/utils/helpers';
import { checkPermission } from '@/utils/permission';

/**
 * CustomerSelect Component (Function Component + Redux Hooks)
 *
 * A reusable customer selection dropdown using Ant Design's Select component.
 * Supports dynamic search, infinite scroll, and formatting based on permissions.
 *
 * Tính năng chính:
 * - Tìm kiếm khách hàng theo tên, mã hoặc số điện thoại (3 số cuối)
 * - Tự động load thêm khách hàng khi scroll xuống cuối danh sách (pagination)
 * - Label hiển thị dạng: "Tên khách hàng (Mã, SĐT hoặc *** che nếu không có quyền)"
 * - Hỗ trợ đầu vào dạng:
 *    - `{ value, label }` (chuẩn `labelInValue`)
 *    - `{ id, full_name, code }` (tự động chuyển đổi)
 * - Nếu khách hàng được chọn không có trong danh sách `options`, sẽ được thêm tạm để giữ selected
 *
 * Kết nối Redux thông qua:
 * - `useSelector()` để lấy danh sách khách hàng & phân trang
 * - `useDispatch()` để gọi `fetchActiveCustomers({ keyword, page })`
 *
 * Component tương thích tốt với Form.create từ Ant Design (`getFieldDecorator`)
 *
 * @component
 *
 * @example
 * <Form.Item label="Khách hàng">
 *   {getFieldDecorator('customer_id', {
 *     rules: [{ required: true, message: 'Trường bắt buộc!' }],
 *     initialValue: currentItem?.customer ?? null
 *   })(
 *     <CustomerSelect
 *       onChange={(id, label) => setSelectedCustomer(id, label)}
 *     />
 *   )}
 * </Form.Item>
 *
 * @param {object} props
 * @param {object} props.value - Giá trị hiện tại của Select. Dạng `{ value, label }` hoặc `{ id, full_name, code }`
 * @param {function} props.onChange - Callback khi chọn hoặc xoá khách hàng. Trả về `(id, label)`
 * @param {string} [props.defaultText] - Hiển thị label mặc định nếu muốn (vd: "Chọn khách hàng")
 * @param {boolean} [props.isLoading=false] - Trạng thái loading từ phía ngoài (tùy chọn)
 *
 * Redux sử dụng:
 * - state.customerReducer.activeList.items → danh sách khách hàng
 * - state.customerReducer.activeList.pagination → dữ liệu phân trang
 * - state.customerReducer.activeList.loading → trạng thái loading
 *
 * Ghi chú:
 * - Component dùng `labelInValue` ⇒ `value` phải là object `{ value, label }`
 * - Sử dụng `checkPermission()` để hiển thị hoặc ẩn số điện thoại thật
 */
const CustomerSelect = ({
    value,
    onChange,
    defaultText,
    labelFormat = 'default',
    isLoading: propLoading = false,
    ...restProps
}) => {
    const dispatch = useDispatch();

    const [keyword, setKeyword] = useState('');

    const options = useSelector(state => state.customerReducer.activeList.items);
    const pagination = useSelector(state => state.customerReducer.activeList.pagination);

    const isLoading = useSelector(state => state.customerReducer.activeList.loading) || propLoading;

    useEffect(() => {
        dispatch(fetchActiveCustomers({ keyword: '', page: 1 }));
    }, [dispatch]);

    const handleSearch = useCallback((val) => {
        setKeyword(val);
        dispatch(fetchActiveCustomers({ keyword: val }));
    }, [dispatch]);

    const handleScroll = useCallback((e) => {
        e.preventDefault();
        //if (pagination?.currentPage && pagination?.currentPage < pagination?.totalPages) {
        dispatch(fetchActiveCustomers({ keyword, page: pagination.currentPage + 1 }));
        //}
    }, [dispatch, keyword, pagination]);

    const getDisplayPhone = (phones) => {
        if (!Array.isArray(phones) || phones.length === 0) return '';

        // 1 số -> lấy luôn
        if (phones.length === 1) return phones[0]?.phone ?? '';

        // >= 2 số -> ưu tiên primary
        const primary = phones.find(p => p?.is_primary && p?.phone);
        if (primary) return primary.phone;

        // không có primary -> lấy số mới nhất
        // ưu tiên theo created_at nếu có, fallback lấy phần tử cuối
        const hasCreatedAt = phones.some(p => p?.created_at);
        if (hasCreatedAt) {
            const newest = [...phones]
                .filter(p => p?.phone)
                .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
                .pop();
            return newest?.phone ?? '';
        }

        // fallback: số cuối cùng trong mảng (thường là mới nhất)
        return phones.filter(p => p?.phone).pop()?.phone ?? '';
    };

    const formatLabel = (option) => {
        const name = option?.full_name ?? '';
        const code = option?.code ?? '';
        if (labelFormat === 'name-code') return code ? `${name} - ${code}` : name;
        const phone = getDisplayPhone(option?.phones);

        let extra = code;

        if (phone) {
            extra += ', ' + (checkPermission('customer', 'phonenumber')
                ? phone
                : repeatPhoneNumber(phone)
            );
        }

        return extra ? `${name} (${extra})` : name;
    };

    // Xử lý merge value nếu không nằm trong danh sách
    const selectedId = value?.value;
    const selectedLabel = value?.label;
    const exists = options.find((opt) => opt.id === selectedId);
    const mergedOptions = exists || !selectedId
        ? options
        : [{ id: selectedId, full_name: selectedLabel }].concat(options);

    return (
        <Select
            showSearch
            allowClear
            labelInValue
            value={value ?? null}
            onChange={(selected) => {
                if (onChange) {
                    onChange(selected?.value ?? null, selected?.label ?? null);
                }

                // Nếu clear -> reset keyword và fetch lại danh sách
                if (selected === null) {
                    setKeyword('');
                    dispatch(fetchActiveCustomers({ keyword: '', page: 1 }));
                }
            }}
            onSearch={handleSearch}
            onPopupScroll={handleScroll}
            filterOption={false}
            loading={isLoading}
            placeholder="Mã, tên, 3 số cuối sđt của khách hàng"
            options={[
                ...(defaultText ? [{ value: null, label: defaultText }] : []),
                ...mergedOptions.map((option, index) => ({
                    key: `${option.id}_${index}`,
                    value: option.id,
                    label: formatLabel(option),
                })),
            ]}
            {...restProps}
        />
    );
};

CustomerSelect.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    defaultText: PropTypes.string,
    labelFormat: PropTypes.oneOf(['default', 'name-code']),
    isLoading: PropTypes.bool,
};

export default CustomerSelect;
